/**
 * Utility functions for checking feature access
 * Supports both old format (string[]) and new format ([{featureName, accessLevel, saveForFuture}])
 */

const normalizeFeature = (value = "") =>
  String(value)
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, " ")
    .replace(/\s+/g, " ");

const getFeatureNameFromItem = (item) => {
  if (!item) return "";
  if (typeof item === "string") {
    return item;
  }
  if (typeof item === "object") {
    if (item.featureName) {
      return item.featureName;
    }
    // Fallback: handle String objects like {0:'M',1:'Y',...}
    const charKeys = Object.keys(item)
      .filter((k) => /^\d+$/.test(k))
      .sort((a, b) => Number(a) - Number(b));
    if (charKeys.length) {
      return charKeys.map((k) => item[k]).join("");
    }
  }
  return "";
};

const isFeatureMatch = (featureToCheck, candidateFeature) => {
  const requested = normalizeFeature(featureToCheck);
  const candidate = normalizeFeature(candidateFeature);
  if (!requested || !candidate) return false;
  if (requested === candidate) return true;
  // Allow parent feature checks, e.g. "POTENTIAL STATUS" -> "POTENTIAL STATUS GRAPHS"
  if (candidate.startsWith(`${requested} `)) return true;
  return false;
};

const implicitParentFor = (featureName) => {
  const f = normalizeFeature(featureName);
  // For these dashboards: Download/Filter should be implicitly allowed
  // whenever the Graphs permission is allowed.
  const map = {
    // Follow-up table: Download is implicit with Follow-up table access
    "FOLLOW-UP-TABLE DOWNLOAD": "FOLLOW-UP-TABLE",
    "POTENTIAL STATUS DOWNLOAD": "POTENTIAL STATUS GRAPHS",
    "POTENTIAL STATUS FILTER": "POTENTIAL STATUS GRAPHS",
    "PROBABILITY STATUS DOWNLOAD": "PROBABILITY STATUS GRAPHS",
    "PROBABILITY STATUS FILTER": "PROBABILITY STATUS GRAPHS",
    "LEAD SOURCE DOWNLOAD": "LEAD SOURCE GRAPHS",
    "LEAD SOURCE FILTER": "LEAD SOURCE GRAPHS",
    "HISTORICAL DATA DOWNLOAD": "HISTORICAL DATA GRAPHS",
    "HISTORICAL DATA FILTER": "HISTORICAL DATA GRAPHS",
    "LEAD CONVERSION RATE FILTER": "LEAD CONVERSION RATE GRAPHS",
    "REVENUE DISTRIBUTION FILTER": "REVENUE DISTRIBUTION GRAPHS",
    // Revenue Distribution should implicitly include these modules
    "TOP 10 CLIENT GRAPHS": "REVENUE DISTRIBUTION GRAPHS",
    "TOP 10 CLIENT DOWNLOAD": "REVENUE DISTRIBUTION GRAPHS",
    "TOP 10 CLIENT FILTER": "REVENUE DISTRIBUTION GRAPHS",
    "REVENUE GRAPHS": "REVENUE DISTRIBUTION GRAPHS",
    "REVENUE DOWNLOAD": "REVENUE DISTRIBUTION GRAPHS",
    "REVENUE FILTER": "REVENUE DISTRIBUTION GRAPHS",
  };
  return map[f] || null;
};

/**
 * Check if an employee has access to a specific feature
 * @param {Object} employee - The employee object
 * @param {string} featureName - The feature name to check (e.g., "MY REQUEST")
 * @returns {boolean} - True if employee has access to the feature
 */
export const hasFeatureAccess = (employee, featureName) => {
  if (!employee || !employee.featureAccess || !Array.isArray(employee.featureAccess)) {
    return false;
  }

  const direct = employee.featureAccess.some((item) => {
    const itemFeatureName = getFeatureNameFromItem(item);
    if (!isFeatureMatch(featureName, itemFeatureName)) {
      return false;
    }
    if (typeof item === "string") {
      return true;
    }
    const level = String(item?.accessLevel || "Full Access").trim().toUpperCase();
    return level === "FULL ACCESS";
  });
  if (direct) return true;
  const implicit = implicitParentFor(featureName);
  if (!implicit) return false;
  return employee.featureAccess.some((item) => {
    const itemFeatureName = getFeatureNameFromItem(item);
    if (!isFeatureMatch(implicit, itemFeatureName)) return false;
    if (typeof item === "string") return true;
    const level = String(item?.accessLevel || "Full Access").trim().toUpperCase();
    return level === "FULL ACCESS";
  });
};

/**
 * Get the access level for a specific feature
 * @param {Object} employee - The employee object
 * @param {string} featureName - The feature name to check
 * @returns {string|null} - The access level ("Full Access", "View Only", "No Access") or null if not found
 */
export const getFeatureAccessLevel = (employee, featureName) => {
  if (!employee || !employee.featureAccess || !Array.isArray(employee.featureAccess)) {
    return null;
  }

  for (const item of employee.featureAccess) {
    const itemFeatureName = getFeatureNameFromItem(item);
    if (isFeatureMatch(featureName, itemFeatureName)) {
      if (typeof item === "string") {
        return "Full Access";
      }
      return item?.accessLevel || "Full Access";
    }
  }

  const implicit = implicitParentFor(featureName);
  if (implicit) {
    for (const item of employee.featureAccess) {
      const itemFeatureName = getFeatureNameFromItem(item);
      if (isFeatureMatch(implicit, itemFeatureName)) {
        if (typeof item === "string") {
          return "Full Access";
        }
        return item?.accessLevel || "Full Access";
      }
    }
  }

  return null;
};

/**
 * Get access level by exact feature name match only (no parent/prefix inference).
 * Useful when permission must be strictly module-scoped.
 * @param {Object} employee
 * @param {string} featureName
 * @returns {string|null}
 */
export const getExactFeatureAccessLevel = (employee, featureName) => {
  if (!employee || !employee.featureAccess || !Array.isArray(employee.featureAccess)) {
    return null;
  }

  const requested = normalizeFeature(featureName);
  if (!requested) return null;

  for (const item of employee.featureAccess) {
    const itemFeatureName = normalizeFeature(getFeatureNameFromItem(item));
    if (itemFeatureName === requested) {
      if (typeof item === "string") {
        return "Full Access";
      }
      return item?.accessLevel || "Full Access";
    }
  }

  return null;
};

/**
 * Check if an employee has View Only or Full Access to a feature (not No Access / not present).
 * Use for conditional display (e.g. show Operation Tracker only when user has some access).
 * @param {Object} employee - The employee object
 * @param {string} featureName - The feature name to check
 * @returns {boolean} - True if feature has "View Only" or "Full Access"
 */
export const hasViewOrFullAccess = (employee, featureName) => {
  const level = getFeatureAccessLevel(employee, featureName);
  if (!level) return false;
  const normalized = String(level).trim().toUpperCase();
  return normalized === "VIEW ONLY" || normalized === "FULL ACCESS";
};

